import { useState } from 'react';
import {
  Loader2,
  CalendarClock,
  Plus,
  Play,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { SlideOver } from '@/components/ui/SlideOver';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useScheduledReports,
  useCreateScheduledReport,
  useUpdateScheduledReport,
  useDeleteScheduledReport,
  useRunScheduledReport,
} from '@/hooks/api/useReportsDepth';
import { ReportFrequency, ReportRunStatus } from '@pms/shared';
import type { ScheduledReport } from '@pms/shared';

const REPORT_TYPE_OPTIONS = [
  'occupancy_revenue',
  'rooms_sold',
  'booked_reservations',
  'arrivals_list',
  'departures_list',
  'housekeeping_summary',
  'maintenance_activity',
  'guest_balance_due',
];

const FREQUENCY_LABELS: Record<ReportFrequency, string> = {
  [ReportFrequency.DAILY]: 'Daily',
  [ReportFrequency.WEEKLY]: 'Weekly',
  [ReportFrequency.MONTHLY]: 'Monthly',
};

function formatDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '--';
}

function RunStatusBadge({ status }: { status: ReportRunStatus | null }) {
  if (!status) {
    return <span className="text-xs text-gray-400">Never run</span>;
  }
  const map: Record<ReportRunStatus, { cls: string; icon: typeof Clock; label: string }> = {
    [ReportRunStatus.QUEUED]: { cls: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Queued' },
    [ReportRunStatus.RUNNING]: { cls: 'bg-blue-50 text-blue-600', icon: Loader2, label: 'Running' },
    [ReportRunStatus.COMPLETED]: { cls: 'bg-green-50 text-green-600', icon: CheckCircle2, label: 'Completed' },
    [ReportRunStatus.FAILED]: { cls: 'bg-red-50 text-red-600', icon: XCircle, label: 'Failed' },
  };
  const { cls, icon: Icon, label } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      <Icon size={12} aria-hidden="true" />
      {label}
    </span>
  );
}

export function ReportsSchedulerPage() {
  const propertyId = useSelectedPropertyId();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduledReport | null>(null);

  const { data: schedules, isLoading } = useScheduledReports(propertyId);
  const deleteMutation = useDeleteScheduledReport();
  const runMutation = useRunScheduledReport();

  const openCreate = () => {
    setEditing(null);
    setPanelOpen(true);
  };
  const openEdit = (s: ScheduledReport) => {
    setEditing(s);
    setPanelOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Reports Scheduler"
        description="Schedule recurring reports and track their last-run status"
        actions={
          <Button onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            New Schedule
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : !schedules || schedules.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No scheduled reports"
          description="Create a schedule to have reports generated automatically."
          actionLabel="New Schedule"
          onAction={openCreate}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Report', 'Frequency', 'Active', 'Last run', 'Status', 'Next run', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {schedules.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-gray-600">{s.reportType.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-gray-600">{FREQUENCY_LABELS[s.frequency]}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${s.isActive ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                      {s.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDateTime(s.lastRunAt)}</td>
                  <td className="px-4 py-3"><RunStatusBadge status={s.lastRunStatus} /></td>
                  <td className="px-4 py-3 text-gray-600">{formatDateTime(s.nextRunAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        isLoading={runMutation.isPending && runMutation.variables === s.id}
                        onClick={() => runMutation.mutate(s.id)}
                        aria-label={`Run ${s.name} now`}
                      >
                        <Play size={14} aria-hidden="true" />
                        Run now
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} aria-label={`Edit ${s.name}`}>
                        <Pencil size={14} aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm(`Delete schedule "${s.name}"?`)) {
                            deleteMutation.mutate(s.id);
                          }
                        }}
                        aria-label={`Delete ${s.name}`}
                      >
                        <Trash2 size={14} className="text-red-500" aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlideOver
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={editing ? 'Edit Schedule' : 'New Schedule'}
        width="max-w-lg"
      >
        <ScheduleForm
          key={editing?.id ?? 'new'}
          propertyId={propertyId}
          editing={editing}
          onDone={() => setPanelOpen(false)}
        />
      </SlideOver>
    </div>
  );
}

function ScheduleForm({
  propertyId,
  editing,
  onDone,
}: {
  propertyId: string;
  editing: ScheduledReport | null;
  onDone: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [reportType, setReportType] = useState(editing?.reportType ?? REPORT_TYPE_OPTIONS[0]!);
  const [frequency, setFrequency] = useState<ReportFrequency>(editing?.frequency ?? ReportFrequency.DAILY);
  const [recipients, setRecipients] = useState((editing?.recipients ?? []).join(', '));
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateScheduledReport();
  const updateMutation = useUpdateScheduledReport();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const recipientList = recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);

    if (editing) {
      updateMutation.mutate(
        { id: editing.id, payload: { name, reportType, frequency, recipients: recipientList, isActive } },
        { onSuccess: onDone },
      );
    } else {
      createMutation.mutate(
        { propertyId, name, reportType, frequency, recipients: recipientList, isActive },
        { onSuccess: onDone },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="sched-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          id="sched-name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Weekly occupancy summary"
        />
      </div>

      <div>
        <label htmlFor="sched-type" className="block text-sm font-medium text-gray-700 mb-1">Report type</label>
        <select
          id="sched-type"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {REPORT_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="sched-freq" className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
        <select
          id="sched-freq"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as ReportFrequency)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {Object.values(ReportFrequency).map((f) => (
            <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="sched-recipients" className="block text-sm font-medium text-gray-700 mb-1">
          Recipients <span className="text-gray-400 font-normal">(comma-separated emails)</span>
        </label>
        <input
          id="sched-recipients"
          type="text"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="ops@example.com, owner@example.com"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Active
      </label>

      <div className="flex gap-2 pt-2">
        <Button type="submit" isLoading={isPending} fullWidth>
          {editing ? 'Save changes' : 'Create schedule'}
        </Button>
      </div>
    </form>
  );
}
